import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth-store';
import {
  LayoutDashboard,
  FolderKanban,
  CheckSquare,
  Clock,
  Users,
  Users2,
  FileText,
  Ticket,
  Receipt,
  BarChart3,
  Settings,
  Megaphone,
  Activity,
  Search,
  Command,
  ArrowRight,
  type LucideIcon,
} from 'lucide-react';

interface CommandItem {
  id: string;
  label: string;
  href: string;
  icon: LucideIcon;
  keywords: string[];
  roles?: string[];
}

const commandItems: CommandItem[] = [
  { id: 'dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, keywords: ['home', 'overview', 'main'] },
  { id: 'projects', label: 'Projects', href: '/projects', icon: FolderKanban, keywords: ['project', 'portfolio'] },
  { id: 'tasks', label: 'Tasks', href: '/tasks', icon: CheckSquare, keywords: ['task', 'todo', 'kanban'] },
  { id: 'time', label: 'Time Tracking', href: '/time', icon: Clock, keywords: ['time', 'timesheet', 'hours', 'clock'], roles: ['admin', 'project_manager'] },
  { id: 'users', label: 'Users', href: '/users', icon: Users, keywords: ['user', 'people', 'team', 'staff'], roles: ['admin'] },
  { id: 'leads', label: 'Leads', href: '/leads', icon: Megaphone, keywords: ['lead', 'prospect', 'crm', 'sales'], roles: ['admin', 'project_manager'] },
  { id: 'clients', label: 'Clients', href: '/clients', icon: Users2, keywords: ['client', 'customer', 'account'], roles: ['admin', 'project_manager'] },
  { id: 'tickets', label: 'Tickets', href: '/tickets', icon: Ticket, keywords: ['ticket', 'support', 'bug'] },
  { id: 'invoices', label: 'Invoices', href: '/invoices', icon: Receipt, keywords: ['invoice', 'bill', 'payment', 'stripe'], roles: ['admin', 'project_manager', 'support_staff'] },
  { id: 'activity', label: 'Activity', href: '/activity', icon: Activity, keywords: ['activity', 'log', 'history', 'feed'] },
  { id: 'reports', label: 'Reports', href: '/reports', icon: BarChart3, keywords: ['report', 'analytics', 'stats', 'metrics'], roles: ['admin', 'project_manager'] },
  { id: 'profile', label: 'Profile', href: '/profile', icon: Users, keywords: ['profile', 'account', 'settings', 'password'] },
  { id: 'settings', label: 'Settings', href: '/settings', icon: Settings, keywords: ['settings', 'preferences', 'config'], roles: ['admin'] },
];

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { user } = useAuthStore();

  const filteredItems = useMemo(() => {
    const lower = query.toLowerCase().trim();
    return commandItems.filter((item) => {
      // Role filter
      if (item.roles && !item.roles.includes(user?.role || '')) return false;
      // Search filter
      if (!lower) return true;
      if (item.label.toLowerCase().includes(lower)) return true;
      return item.keywords.some((kw) => kw.includes(lower));
    });
  }, [query, user?.role]);

  // Reset state when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelectedIndex(0);
      // Focus input after animation frame so the dialog is mounted
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev + 1) % Math.max(filteredItems.length, 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev - 1 + filteredItems.length) % Math.max(filteredItems.length, 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (filteredItems[selectedIndex]) {
            navigate(filteredItems[selectedIndex].href);
            onOpenChange(false);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onOpenChange(false);
          break;
      }
    },
    [filteredItems, selectedIndex, navigate, onOpenChange],
  );

  // Scroll selected item into view
  useEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const item = list.children[selectedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: 'nearest' });
  }, [selectedIndex]);

  // Global Cmd+K listener + custom event for external toggle buttons
  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    const toggleHandler = () => {
      onOpenChange(!open);
    };
    document.addEventListener('keydown', keyHandler);
    window.addEventListener('toggle-command-palette', toggleHandler);
    return () => {
      document.removeEventListener('keydown', keyHandler);
      window.removeEventListener('toggle-command-palette', toggleHandler);
    };
  }, [open, onOpenChange]);

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[80] bg-black/50 backdrop-blur-sm"
        onClick={() => onOpenChange(false)}
      />

      {/* Palette */}
      <div
        className="fixed left-1/2 top-[15%] z-[90] w-full max-w-xl -translate-x-1/2"
        onKeyDown={handleKeyDown}
      >
        <div className="rounded-2xl border border-[var(--border-color)] bg-[var(--bg-primary)] shadow-2xl overflow-hidden">
          {/* Search input */}
          <div className="flex items-center gap-3 px-4 border-b border-[var(--border-color)]">
            <Search size={18} className="text-[var(--text-secondary)] flex-shrink-0" />
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder={t('common.searchPlaceholder', 'Search pages, projects, actions...')}
              className="flex-1 h-14 bg-transparent text-[var(--text-primary)] text-base outline-none placeholder:text-[var(--text-secondary)]"
            />
            <kbd className="hidden sm:inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-md bg-[var(--bg-secondary)] text-[var(--text-secondary)] border border-[var(--border-color)]">
              <Command size={12} />
              K
            </kbd>
          </div>

          {/* Results */}
          <div ref={listRef} className="max-h-[300px] overflow-y-auto p-2 space-y-0.5">
            {filteredItems.length === 0 ? (
              <div className="py-8 text-center">
                <p className="text-sm text-[var(--text-secondary)]">{t('common.noResults', 'No results found')}</p>
              </div>
            ) : (
              filteredItems.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => {
                    navigate(item.href);
                    onOpenChange(false);
                  }}
                  onMouseEnter={() => setSelectedIndex(index)}
                  className={cn(
                    'flex items-center gap-3 w-full px-3 py-2.5 rounded-lg text-sm text-left transition-colors',
                    index === selectedIndex
                      ? 'bg-dyndev-green/10 text-dyndev-green'
                      : 'text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]',
                  )}
                >
                  <item.icon size={18} className="flex-shrink-0" />
                  <span className="flex-1">{item.label}</span>
                  <ArrowRight
                    size={14}
                    className={cn(
                      'flex-shrink-0 transition-opacity',
                      index === selectedIndex ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                </button>
              ))
            )}
          </div>

          {/* Footer hint */}
          <div className="flex items-center gap-4 px-4 py-2.5 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]/50">
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
              <kbd className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--bg-primary)] border border-[var(--border-color)]">↑↓</kbd>
              <span>Navigate</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
              <kbd className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--bg-primary)] border border-[var(--border-color)]">↵</kbd>
              <span>Open</span>
            </div>
            <div className="flex items-center gap-1.5 text-[11px] text-[var(--text-secondary)]">
              <kbd className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-[var(--bg-primary)] border border-[var(--border-color)]">Esc</kbd>
              <span>Close</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
